import { CartButton } from "@/components/CartButton";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Menu, Sparkles, X } from "lucide-react";
import { useRef, useState } from "react";

const primaryLinks = [
  { href: "/shop", label: "Shop all" },
  { href: "/gummies", label: "Gummies" },
  { href: "/powders", label: "Powder" },
  { href: "/capsules", label: "Capsules" },
  { href: "/krypt", label: "KRYPT" },
] as const;

const secondaryLinks = [
  { href: "/wholesale", label: "Wholesale" },
  { href: "/about", label: "About Kratown" },
  { href: "/learn", label: "Learn" },
  { href: "/shipping-restrictions", label: "Shipping restrictions" },
] as const;

function NavLink({ href, label }: { href: string; label: string }) {
  return (
    <SheetClose asChild>
      <a
        href={href}
        className="flex min-h-12 items-center rounded-xl border border-white/8 bg-white/[0.035] px-4 text-sm font-medium text-white/88 transition hover:border-[#c2ff45]/35 hover:bg-[#c2ff45]/10 hover:text-[#e6ffae] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#c2ff45]"
      >
        {label}
      </a>
    </SheetClose>
  );
}

export function MobileStorefrontHeader() {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);
    if (!nextOpen) {
      requestAnimationFrame(() => triggerRef.current?.focus());
    }
  };

  return (
    <header data-mobile-storefront-header className="sticky top-0 z-40 border-b border-white/10 bg-[#08110f]/92 backdrop-blur-2xl lg:hidden">
      <div className="container flex min-h-16 items-center justify-between gap-2 py-2">
        <a
          href="/"
          aria-label="Kratown home"
          className="flex min-w-0 items-center gap-2.5 rounded-lg pr-2 text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#c2ff45]"
        >
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-[#c2ff45]/30 bg-[#0d1916] text-[#c2ff45] shadow-[0_0_22px_rgba(194,255,69,0.16)]">
            <Sparkles className="h-4 w-4" aria-hidden="true" />
          </span>
          <span className="min-w-0 font-serif text-2xl font-semibold uppercase leading-none tracking-[0.02em] text-white">Kratown</span>
        </a>

        <div className="flex shrink-0 items-center gap-2">
          <CartButton className="min-h-10 border-white/12 bg-white/5 px-3 text-sm text-white hover:bg-white/10" />
          <Sheet open={open} onOpenChange={handleOpenChange}>
            <SheetTrigger asChild>
              <button
                ref={triggerRef}
                type="button"
                className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-[#c2ff45]/28 bg-[#0d1916] text-[#d9ff8a] transition hover:border-[#c2ff45]/55 hover:bg-[#c2ff45]/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#c2ff45]"
                aria-controls="mobile-storefront-navigation"
                aria-expanded={open}
                aria-label={open ? "Close site navigation" : "Open site navigation"}
              >
                <Menu className="h-5 w-5" aria-hidden="true" />
              </button>
            </SheetTrigger>
            <SheetContent
              side="right"
              showCloseButton={false}
              onEscapeKeyDown={() => handleOpenChange(false)}
              className="w-[min(22rem,calc(100vw-1rem))] gap-0 overflow-y-auto border-l border-[#c2ff45]/20 bg-[#07110f] p-0 text-white"
            >
              <SheetClose asChild>
                <button
                  type="button"
                  data-mobile-nav-close
                  aria-label="Close site navigation"
                  className="absolute right-3 top-3 z-10 inline-flex h-10 w-10 items-center justify-center rounded-xl border border-[#c2ff45]/28 bg-[#0d1916] text-[#d9ff8a] transition hover:border-[#c2ff45]/55 hover:bg-[#c2ff45]/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#c2ff45]"
                >
                  <X className="h-5 w-5" aria-hidden="true" />
                </button>
              </SheetClose>
              <SheetHeader className="border-b border-white/10 px-5 py-6 text-left">
                <div className="flex items-center gap-3 pr-9">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-[#c2ff45]/30 bg-[#0d1916] text-[#c2ff45]">
                    <Sparkles className="h-4 w-4" aria-hidden="true" />
                  </span>
                  <div>
                    <SheetTitle className="font-serif text-2xl uppercase tracking-[0.02em] text-white">Kratown</SheetTitle>
                    <SheetDescription className="mt-1 text-[11px] uppercase tracking-[0.18em] text-white/55">Shop and account navigation</SheetDescription>
                  </div>
                </div>
              </SheetHeader>

              <nav id="mobile-storefront-navigation" className="space-y-6 px-5 py-6" aria-label="Mobile storefront navigation">
                <section>
                  <p className="px-1 text-[11px] font-semibold uppercase tracking-[0.2em] text-[#d9ff8a]">Shop</p>
                  <div className="mt-3 grid gap-2">
                    {primaryLinks.map((link) => <NavLink key={link.href} {...link} />)}
                  </div>
                </section>
                <section className="border-t border-white/10 pt-6">
                  <p className="px-1 text-[11px] font-semibold uppercase tracking-[0.2em] text-[#8beeff]">More from Kratown</p>
                  <div className="mt-3 grid gap-2">
                    {secondaryLinks.map((link) => <NavLink key={link.href} {...link} />)}
                  </div>
                </section>
              </nav>
              <p className="mt-auto border-t border-white/10 px-6 py-5 text-xs leading-5 text-white/56">Kratown products are for adults 21 and older. Review shipping restrictions before ordering.</p>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
